import { LocalNotifications, DeliveredNotificationSchema } from '@capacitor/local-notifications';
import { getPlatformOS } from '../../../utils/platformUtils';
import { PushNotifications } from '@capacitor/push-notifications';
import {
  PlatformAdapter,
  PermissionStatus,
  NotificationOptions,
  NotificationChannel,
  PendingNotification,
  DeliveredNotification
} from '../types';

/**
 * Capacitor通知适配器 (iOS/Android)
 */
export class CapacitorNotificationAdapter implements PlatformAdapter {
  private defaultChannelId = 'default_channel';
  private defaultChannelName = '默认通知';

  constructor() {
    // 初始化时创建默认通知频道 (Android)
    if (getPlatformOS() === 'android') {
      this.createDefaultChannel();
    }
  }

  /**
   * 创建默认通知频道
   */
  private async createDefaultChannel() {
    try {
      await LocalNotifications.createChannel({
        id: this.defaultChannelId,
        name: this.defaultChannelName,
        importance: 3, // 默认重要性
        visibility: 1, // 锁屏可见
        lights: true,
        vibration: true
      });
    } catch (error) {
      console.error('[CapacitorAdapter] Error creating default channel:', error);
    }
  }

  /**
   * 创建通知频道
   */
  async createChannel(channel: NotificationChannel): Promise<void> {
    if (getPlatformOS() !== 'android') return;

    try {
      await LocalNotifications.createChannel({
        id: channel.id,
        name: channel.name,
        description: channel.description,
        importance: channel.importance,
        visibility: channel.visibility,
        sound: channel.sound,
        lights: channel.lights,
        vibration: channel.vibration
      });
    } catch (error) {
      console.error('[CapacitorAdapter] Error creating channel:', error);
      throw error;
    }
  }

  /**
   * 请求通知权限
   */
  async requestPermission(): Promise<PermissionStatus> {
    try {
      // 初始化Capacitor本地通知权限
      const result = await LocalNotifications.requestPermissions();
      if (result.display !== 'granted') {
        console.warn('[CapacitorAdapter] Local notification permission denied');
      }

      // 初始化Capacitor推送通知权限
      const pushResult = await PushNotifications.requestPermissions();
      if (pushResult.receive !== 'granted') {
        console.warn('[CapacitorAdapter] Push notification permission denied');
      }

      // 如果本地通知或推送通知任何一个授权，则返回授权
      if (result.display === 'granted' || pushResult.receive === 'granted') {
        return 'granted';
      }

      return 'denied';
    } catch (error) {
      console.error('[CapacitorAdapter] Permission request error:', error);
      return 'denied';
    }
  }

  /**
   * 检查通知权限
   */
  async checkPermission(): Promise<PermissionStatus> {
    try {
      const result = await LocalNotifications.checkPermissions();
      const pushResult = await PushNotifications.checkPermissions();

      if (result.display === 'granted' || pushResult.receive === 'granted') {
        return 'granted';
      }

      return 'denied';
    } catch (error) {
      console.error('[CapacitorAdapter] Permission check error:', error);
      return 'denied';
    }
  }

  /**
   * 转换通知选项为Capacitor格式
   */
  private convertToCapacitorOptions(options: NotificationOptions): any {
    // 确保ID是数字
    let numericId: number;

    if (typeof options.id === 'string') {
      numericId = parseInt(options.id, 10);
      if (isNaN(numericId)) {
        numericId = Math.floor(Math.random() * 1000000);
      }
    } else if (typeof options.id === 'number') {
      numericId = options.id;
    } else {
      numericId = Math.floor(Math.random() * 1000000);
    }

    // 基本通知选项
    const notification: any = {
      id: numericId,
      title: options.title,
      body: options.body,
      channelId: this.defaultChannelId,
      sound: typeof options.sound === 'boolean' ? (options.sound ? 'default' : undefined) : options.sound,
      extra: options.extra || {}
    };

    // 添加调度（如果有）
    if (options.schedule) {
      notification.schedule = {
        at: options.schedule.at,
        // 使用every替代repeating，Capacitor不直接支持repeating
        every: options.schedule.every,
        count: options.schedule.count
      };
    }

    // 添加操作类型（如果有）
    if (options.actionTypeId) {
      notification.actionTypeId = options.actionTypeId;
    }

    // 添加分组（如果有）
    if (options.groupId) {
      notification.group = options.groupId;
      if (options.groupSummary) {
        notification.groupSummary = options.groupSummary;
      }
    }

    // 添加Android特有属性（如果有）
    if (getPlatformOS() === 'android') {
      if (options.ongoing !== undefined) {
        notification.ongoing = options.ongoing;
      }
      if (options.autoCancel !== undefined) {
        notification.autoCancel = options.autoCancel;
      }
    }

    return notification;
  }

  /**
   * 发送本地通知
   */
  async scheduleLocalNotification(options: NotificationOptions): Promise<string> {
    try {
      console.log('[CapacitorAdapter] Scheduling local notification with options:', options);

      const notification = this.convertToCapacitorOptions(options);
      console.log('[CapacitorAdapter] Converted notification:', notification);

      // 使用Capacitor发送通知
      const result = await LocalNotifications.schedule({
        notifications: [notification]
      });

      console.log('[CapacitorAdapter] LocalNotifications.schedule result:', result);
      console.log('[CapacitorAdapter] Notification scheduled successfully with ID:', notification.id);

      return notification.id.toString();
    } catch (error) {
      console.error('[CapacitorAdapter] Schedule notification error:', error);
      if (error instanceof Error) {
        console.error('[CapacitorAdapter] Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
      }
      throw error;
    }
  }

  /**
   * 取消本地通知
   */
  async cancelLocalNotification(id: string): Promise<void> {
    try {
      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error('Invalid notification ID');
      }

      await LocalNotifications.cancel({ notifications: [{ id: numericId }] });
    } catch (error) {
      console.error('[CapacitorAdapter] Cancel notification error:', error);
      throw error;
    }
  }

  /**
   * 取消所有本地通知
   */
  async cancelAllLocalNotifications(): Promise<void> {
    try {
      // 获取所有待处理的通知并取消它们
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        const notificationsToCancel = pending.notifications.map(n => ({ id: n.id }));
        await LocalNotifications.cancel({ notifications: notificationsToCancel });
        console.log(`[CapacitorAdapter] Cancelled ${notificationsToCancel.length} pending notifications`);
      } else {
        console.log('[CapacitorAdapter] No pending notifications to cancel');
      }
    } catch (error) {
      console.error('[CapacitorAdapter] Cancel all notifications error:', error);
      throw error;
    }
  }

  /**
   * 注册推送通知
   */
  async registerForPushNotifications(): Promise<string> {
    try {
      // 确保注册FCM/APNs并返回令牌
      await PushNotifications.register();

      // 获取令牌的过程是异步的，Capacitor通过事件提供
      // 此处返回一个可能不准确的占位符，实际令牌应通过监听器获取
      return 'registered-waiting-for-token';
    } catch (error) {
      console.error('[CapacitorAdapter] Register push error:', error);
      throw error;
    }
  }

  /**
   * 注销推送通知
   */
  async unregisterForPushNotifications(): Promise<void> {
    try {
      // Capacitor中没有直接的注销方法，使用临时方案
      // 在后续版本中，Capacitor可能会提供正式的注销API
      await PushNotifications.removeAllListeners();
    } catch (error) {
      console.error('[CapacitorAdapter] Unregister push error:', error);
      throw error;
    }
  }

  /**
   * 获取待处理通知
   */
  async getPendingNotifications(): Promise<PendingNotification[]> {
    try {
      const pendingList = await LocalNotifications.getPending();

      // 转换为通用格式
      return pendingList.notifications.map(notification => ({
        id: notification.id.toString(),
        title: notification.title,
        body: notification.body,
        schedule: notification.schedule,
        extra: notification.extra
      }));
    } catch (error) {
      console.error('[CapacitorAdapter] Get pending notifications error:', error);
      return [];
    }
  }

  /**
   * 获取已递送通知
   */
  async getDeliveredNotifications(): Promise<DeliveredNotification[]> {
    try {
      const deliveredList = await LocalNotifications.getDeliveredNotifications();

      // 转换为通用格式并添加接收时间（虽然Capacitor本身不提供）
      return deliveredList.notifications.map(notification => ({
        id: notification.id.toString(),
        title: notification.title,
        body: notification.body,
        extra: notification.extra,
        deliveredAt: new Date() // 使用当前时间，因为Capacitor不提供实际递送时间
      }));
    } catch (error) {
      console.error('[CapacitorAdapter] Get delivered notifications error:', error);
      return [];
    }
  }

  /**
   * 移除已递送通知
   */
  async removeDeliveredNotifications(ids: string[]): Promise<void> {
    try {
      // 转换ID为数字，并创建格式正确的通知项目
      const notificationsToRemove: DeliveredNotificationSchema[] = ids
        .map(id => {
          const numericId = parseInt(id, 10);
          if (!isNaN(numericId)) {
            return {
              id: numericId,
              title: '',
              body: ''
            };
          }
          return null;
        })
        .filter((item): item is DeliveredNotificationSchema => item !== null);

      if (notificationsToRemove.length === 0) return;

      await LocalNotifications.removeDeliveredNotifications({
        notifications: notificationsToRemove
      });
    } catch (error) {
      console.error('[CapacitorAdapter] Remove delivered notifications error:', error);
      throw error;
    }
  }
}